import { useEffect, useLayoutEffect, useRef } from 'react';

const rowKeys = ['ArrowDown', 'ArrowUp', 'Home', 'End', 'Enter', 'NumpadEnter', 'Escape'];
const searchKeys = ['ArrowDown', 'ArrowUp', 'Escape'];
const handledKeys = keys => keys.map(code => ({ code, ctrlKey: false, altKey: false, metaKey: false, shiftKey: false }));
const modified = event => ['ctrlKey', 'altKey', 'metaKey', 'shiftKey'].some(key => event.nativeEvent[key]);

// Focus the real shared rows, including rows which FlatList has not mounted.
// Navigation never changes selection; Enter uses the row's existing action.
export default function useWindowsModelNavigation({ enabled, items, listRef, onClose }) {
  const nodes = useRef(new Map()), pending = useRef(null), focused = useRef(null), current = useRef(null), mounted = useRef(true);
  const signature = items.map(item => item.key).join('\0');
  current.current = { enabled, items, onClose };
  function cancel() {
    const target = pending.current; pending.current = null;
    if (target?.frame != null) cancelAnimationFrame(target.frame);
    if (target?.timer != null) clearTimeout(target.timer);
  }
  const valid = target => mounted.current && pending.current === target && current.current.enabled && current.current.items.some(item => item.key === target.key);
  function queue(target) {
    if (!valid(target) || target.frame != null) return;
    target.frame = requestAnimationFrame(() => { target.frame = null; attempt(target); });
  }
  function attempt(target) {
    if (!valid(target)) return;
    if (Date.now() >= target.deadline) { cancel(); return; }
    const item = current.current.items.find(item => item.key === target.key);
    if (!item) { cancel(); return; }
    if (item.index == null) listRef.current?.scrollToOffset?.({ offset: 0, animated: false });
    else listRef.current?.scrollToIndex?.({ index: item.index, animated: false, viewPosition: 0.5 });
    const node = nodes.current.get(item.key);
    if (node && focused.current?.key === item.key && focused.current.node === node) { cancel(); return; }
    // A newly mounted Fabric ref can exist before XAML can focus it. Keep the
    // request until onFocus confirms it; onLayout can retry after measurement.
    node?.focus?.();
    if (!valid(target)) return;
    // Large native catalogs need multiple measurement batches. Bound elapsed
    // time rather than consuming the retry budget in ref/layout event bursts.
    const remaining = target.deadline - Date.now();
    if (remaining > 0 && valid(target)) {
      if (target.timer != null) clearTimeout(target.timer);
      target.timer = setTimeout(() => { target.timer = null; queue(target); }, Math.min(50, remaining));
    } else cancel();
  }
  function focus(key) {
    cancel();
    if (!mounted.current || !current.current.enabled || !current.current.items.some(item => item.key === key)) return;
    const target = { key, deadline: Date.now() + 2000, frame: null, timer: null };
    pending.current = target; queue(target);
  }
  useLayoutEffect(() => {
    const target = pending.current;
    if (target) { if (valid(target)) queue(target); else cancel(); }
  }, [enabled, signature]);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; cancel(); }; }, []);
  function keyDown(event, key) {
    if (!mounted.current || !current.current.enabled || modified(event)) { cancel(); return; }
    const code = event.nativeEvent.code || event.nativeEvent.key;
    if (!(key == null ? searchKeys : rowKeys).includes(code)) { cancel(); return; }
    event.stopPropagation?.(); event.preventDefault?.();
    if (code === 'Escape') { cancel(); current.current.onClose(); return; }
    const choices = current.current.items;
    if (!choices.length) return;
    if (code === 'Enter' || code === 'NumpadEnter') {
      const item = choices.find(item => item.key === key);
      cancel(); item?.activate(); return;
    }
    const index = choices.findIndex(item => item.key === (pending.current?.key || key));
    const next = code === 'Home' ? 0 : code === 'End' ? choices.length - 1
      : code === 'ArrowUp' ? index < 0 ? choices.length - 1 : (index - 1 + choices.length) % choices.length
        : (index + 1) % choices.length;
    focus(choices[next].key);
  }
  return {
    cancel,
    searchProps: enabled ? { keyDownEvents: handledKeys(searchKeys), onKeyDown: event => keyDown(event, null), onBlur: cancel } : {},
    rowProps: key => enabled ? {
      ref: node => { if (node) { nodes.current.set(key, node); if (pending.current?.key === key) queue(pending.current); } else nodes.current.delete(key); },
      onFocus: () => {
        const node = nodes.current.get(key);
        if (!mounted.current || !node) return;
        focused.current = { key, node };
        if (pending.current?.key === key) cancel();
      },
      onBlur: () => { if (focused.current?.key === key) focused.current = null; },
      onLayout: () => { if (pending.current?.key === key) queue(pending.current); },
      enableFocusRing: false,
      keyDownEvents: handledKeys(rowKeys), onKeyDown: event => keyDown(event, key),
    } : {},
    listProps: enabled ? { onContentSizeChange: () => { if (pending.current) queue(pending.current); }, onScrollToIndexFailed: info => {
      const target = pending.current;
      const item = target && current.current.items.find(item => item.key === target.key);
      if (!target || !valid(target) || item?.index !== info.index) return;
      if (Number.isFinite(info.averageItemLength) && info.averageItemLength > 0)
        listRef.current?.scrollToOffset?.({ offset: info.averageItemLength * info.index, animated: false });
    } } : {},
  };
}
